import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { MapPin, Users, BarChart3, Globe, ArrowRight, CheckCircle2 } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const TEAM_IMAGE = 'https://images.unsplash.com/photo-1623671986977-2a8add98e79e?w=900&q=80';

export default function About() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const capacityItems = t('about.capacity_items', { returnObjects: true }) || [];
  const values = t('about.values', { returnObjects: true }) || [];

  return (
    <>
      <Helmet>
        <title>About ENERGIESSE SL — B2B Solar Installation Subcontractor</title>
        <meta
          name="description"
          content="ENERGIESSE SL: 6 workers, 2 teams, ~2 installs/day. B2B solar installation subcontractor based in Comunidad Valenciana. Available across Spain and EU."
        />
      </Helmet>

      {/* Page Hero */}
      <section className="pt-14 pb-10 bg-[var(--energy-bg)] border-b border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl space-y-4"
          >
            <motion.p variants={fadeUp} className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
              About
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('about.headline')}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-base text-muted-foreground leading-relaxed max-w-[65ch]"
            >
              {t('about.intro')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Teams Section */}
      <section className="section-padding bg-white">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="space-y-6"
            >
              <motion.div variants={fadeUp} className="space-y-1">
                <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                  {t('about.team_title')}
                </p>
                <h2 className="font-heading text-2xl font-semibold tracking-tight">
                  {t('about.team_subtitle')}
                </h2>
              </motion.div>

              <motion.div variants={stagger} className="space-y-4">
                {[t('about.team_1', { returnObjects: true }), t('about.team_2', { returnObjects: true })].map(
                  (team, i) => (
                    <motion.div key={i} variants={fadeUp}>
                      <Card className="border-border card-hover">
                        <CardContent className="p-5 flex gap-4">
                          <div className="w-10 h-10 rounded-full bg-[var(--energy-green-soft)] flex items-center justify-center flex-shrink-0">
                            <Users className="h-4.5 w-4.5 text-[var(--energy-green)] h-5 w-5" />
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-heading font-semibold text-sm">{team.title}</h3>
                              <Badge variant="secondary" className="text-[10px] bg-muted">
                                {team.role}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground leading-relaxed">{team.desc}</p>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )
                )}
              </motion.div>
            </motion.div>

            {/* Team image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl overflow-hidden aspect-[4/3] shadow-sm"
            >
              <img
                src={TEAM_IMAGE}
                alt="Installation team at work"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Capacity Section */}
      <section className="section-padding bg-[var(--energy-bg)] border-y border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="space-y-8"
          >
            <motion.div variants={fadeUp} className="space-y-1">
              <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                {t('about.capacity_title')}
              </p>
            </motion.div>

            <motion.div
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            >
              {capacityItems.map((item, i) => (
                <motion.div key={i} variants={fadeUp}>
                  <Card className="border-border card-hover h-full">
                    <CardContent className="p-5">
                      <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wide">{item.label}</p>
                      <p className="font-heading font-semibold text-base">{item.value}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Geography Section */}
      <section className="section-padding bg-white">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="space-y-6"
            >
              <motion.div variants={fadeUp} className="space-y-1">
                <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                  {t('about.geography_title')}
                </p>
              </motion.div>

              <motion.div variants={stagger} className="space-y-4">
                {[
                  {
                    icon: MapPin,
                    label: t('about.geography_primary'),
                    value: t('about.geography_primary_value'),
                    highlight: true,
                  },
                  {
                    icon: MapPin,
                    label: t('about.geography_secondary'),
                    value: t('about.geography_secondary_value'),
                  },
                  {
                    icon: Globe,
                    label: t('about.geography_international'),
                    value: t('about.geography_international_value'),
                  },
                ].map((geo, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className={`flex items-start gap-3 p-4 rounded-xl border ${
                      geo.highlight
                        ? 'border-[var(--energy-green)]/30 bg-[var(--energy-green-soft)]'
                        : 'border-border bg-muted/30'
                    }`}
                  >
                    <geo.icon
                      className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                        geo.highlight ? 'text-[var(--energy-green)]' : 'text-muted-foreground'
                      }`}
                    />
                    <div>
                      <p className="text-xs text-muted-foreground">{geo.label}</p>
                      <p className="font-medium text-sm mt-0.5">{geo.value}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Values */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="space-y-6"
            >
              <motion.div variants={fadeUp} className="space-y-1">
                <p className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
                  {t('about.values_title')}
                </p>
              </motion.div>

              <motion.div variants={stagger} className="space-y-3">
                {values.map((val, i) => (
                  <motion.div key={i} variants={fadeUp} className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-[var(--energy-green)] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-heading font-semibold text-sm">{val.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{val.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Compliance note */}
      <section className="py-10 bg-[var(--energy-bg)] border-t border-border">
        <div className="container-site">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35 }}
            className="max-w-2xl space-y-3"
          >
            <h3 className="font-heading font-semibold text-base">{t('about.compliance_title')}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('about.compliance_desc')}</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[hsl(222,22%,10%)]">
        <div className="container-site">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-1">
              <h2 className="font-heading text-xl font-semibold text-white">{t('cta_section.title')}</h2>
              <p className="text-sm text-[hsl(215,12%,55%)]">{t('cta_section.subtitle')}</p>
            </div>
            <Button
              size="lg"
              className="bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary flex-shrink-0"
              onClick={() => navigate('/contact')}
              data-testid="about-cta-button"
            >
              {t('cta_section.cta')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
